import { Controller } from '@hotwired/stimulus'
import { readToolbarContext, writeToolbarContext, addShortcut, bumpToolbarRevision, resolveRedirectUrl } from '../../services'

type StimulusParamsEvent = Event & { params: Record<string, string> }

export default class extends Controller {
  static targets = ['projectItem', 'dialog', 'sectionSelect', 'topRow']

  declare readonly projectItemTargets: HTMLElement[]
  declare readonly dialogTarget: HTMLDialogElement
  declare readonly sectionSelectTarget: HTMLSelectElement
  declare readonly topRowTarget: HTMLElement
  declare readonly hasTopRowTarget: boolean

  private pendingAppId: number | null = null
  private csrf: string | undefined
  private observer: ResizeObserver | null = null

  connect(): void {
    this.pendingAppId = null
    this.csrf = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content

    const color = (this.element as HTMLElement).dataset.toolbarColor
    const container = this.element.closest<HTMLElement>('.project-toolbar__container')
    if (container && color) {
      container.style.setProperty('--loader-base-color', color)
    }

    this.setupLayoutObserver()
  }

  disconnect(): void {
    this.observer?.disconnect()
  }

  changeProject(event: Event): void {
    const target = (event.currentTarget as HTMLElement).closest<HTMLElement>('[data-project-id]')

    const projectId = target?.dataset.projectId
    if (!projectId) return

    if (resolveRedirectUrl(target?.dataset.redirectUrl, projectId)) {
      return
    }

    if (target?.dataset.redirectUrl) {
      loadMainBody(target.dataset.redirectUrl)
    } else {
      const existingCtx = readToolbarContext()
      writeToolbarContext({ project_id: projectId, show: true, redirect_route: existingCtx?.redirect_route })
    }
  }

  async addShortcut(event: Event): Promise<void> {
    const params = (event as StimulusParamsEvent).params
    const slug = this.getCurrentProjectSlug()
    if (!slug || !this.csrf) return

    const body = await addShortcut(slug, this.csrf, { menu_app_id: Number(params.appId) })

    if (!body) {
      showMessageToast('red', 'Something went wrong. Please try again.')
    } else if ('success' in body) {
      bumpToolbarRevision()
    } else if ('require_fallback' in body) {
      this.pendingAppId = Number(params.appId)
      this.dialogTarget.showModal()
    } else {
      showMessageToast('red', `Error: ${body.error || 'Unable to add shortcut'}`)
    }
  }

  async submitShortcut(): Promise<void> {
    const slug = this.getCurrentProjectSlug()
    if (!slug || !this.csrf) return

    const body = await addShortcut(slug, this.csrf, {
      menu_app_id: this.pendingAppId,
      fallback_section_id: this.sectionSelectTarget.value
    })

    if (!body) {
      showMessageToast('red', 'Something went wrong. Please try again.')
    } else if ('success' in body) {
      this.dialogTarget.close()
      bumpToolbarRevision()
    } else if ('error' in body) {
      showMessageToast('red', `Error: ${body.error || 'Unable to add shortcut'}`)
    }

    this.pendingAppId = null
  }

  closeDialog(event: Event): void {
    if (event.target === this.dialogTarget) this.dialogTarget.close()
  }

  searchProjects(event: Event): void {
    const q = (event.currentTarget as HTMLInputElement).value.trim().toLowerCase()
    this.projectItemTargets.forEach(li => {
      li.hidden = !li.dataset.projectName?.toLowerCase().includes(q)
    })
  }

  showHint(event: Event): void {
    const appId = Number((event as StimulusParamsEvent).params.appId)
    document.getElementById(`menu-app-${appId}--hint`)?.showPopover()
  }

  hideHint(event: Event): void {
    const appId = Number((event as StimulusParamsEvent).params.appId)
    document.getElementById(`menu-app-${appId}--hint`)?.hidePopover()
  }

  private getCurrentProjectSlug(): string | undefined {
    return document.querySelector<HTMLElement>('[data-role="apps"]')?.dataset.projectSlug
  }

  private setupLayoutObserver(): void {
    if (!this.hasTopRowTarget) return
    this.observer = new ResizeObserver(() => this.checkLayout())
    this.observer.observe(this.topRowTarget)
    this.checkLayout()
  }

  private checkLayout(): void {
    const topRow = this.topRowTarget
    const title = topRow.querySelector<HTMLElement>('.project-toolbar__project-title')
    const actions = topRow.querySelector<HTMLElement>('.project-toolbar__project-actions')
    const metadata = topRow.querySelector<HTMLElement>('.project-toolbar__project-metadata')

    if (!title || !actions || !metadata) return

    const fitsOnOneLine = title.scrollWidth + actions.scrollWidth <= topRow.offsetWidth
    const isSwapped = title.nextElementSibling === metadata && metadata.nextElementSibling === actions

    if (!fitsOnOneLine && !isSwapped) {
      topRow.insertBefore(metadata, actions)
    } else if (fitsOnOneLine && isSwapped) {
      topRow.appendChild(metadata)
    }
  }
}
